import * as C from "../config/constants.js";
import { getCachedImage } from "../assets/image-cache.js";
import { mergeTierImageUrl } from "../assets/merge-tier-urls.js";

var DEFAULT_IMAGE_BODY_RADIUS_RATIO = 0.45;

/**
 * Canvas-matched sprite box for the merge drop preview (see `assets/merge-assets.js`).
 * @param {number} tier
 * @param {number} bodyRadius physics radius in panel px
 * @returns {{ drawW: number, drawH: number, anchorOffsetX: number, anchorOffsetY: number } | null}
 */
export function getDropPreviewSpriteMetrics(tier, bodyRadius) {
  var R = Number(bodyRadius);
  if (!isFinite(R) || R <= 0) return null;
  var chain = (typeof C.getActiveEvolutionChain === "function")
    ? C.getActiveEvolutionChain()
    : (C.EVOLUTION_CHAIN || []);
  var cfg = chain[tier] || {};
  var ratio = cfg.imageBodyRadiusRatio != null ? Number(cfg.imageBodyRadiusRatio) : DEFAULT_IMAGE_BODY_RADIUS_RATIO;
  if (!isFinite(ratio) || ratio <= 0) ratio = DEFAULT_IMAGE_BODY_RADIUS_RATIO;
  var center = (cfg && cfg.assetCenter) || [0.5, 0.5];
  var cxRatio = center[0] != null ? Number(center[0]) : 0.5;
  var cyRatio = center[1] != null ? Number(center[1]) : 0.5;
  if (!isFinite(cxRatio)) cxRatio = 0.5;
  if (!isFinite(cyRatio)) cyRatio = 0.5;
  var img = getCachedImage(mergeTierImageUrl(cfg.assetId || "cell"));
  var iw = 1;
  var ih = 1;
  if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
    iw = img.naturalWidth;
    ih = img.naturalHeight;
  }
  var maxSize = R / ratio;
  var scale = maxSize / Math.max(iw, ih);
  var drawW = iw * scale;
  var drawH = ih * scale;
  return {
    drawW: drawW,
    drawH: drawH,
    anchorOffsetX: cxRatio * drawW,
    anchorOffsetY: cyRatio * drawH,
  };
}

/**
 * Panel-space px to subtract from the rim spawn line so physics matches the DOM aim.
 * @param {number} tier
 * @param {number} bodyRadius
 * @param {number} [liftFrac]
 */
export function getDropSpawnLiftPx(tier, bodyRadius, liftFrac) {
  var metrics = getDropPreviewSpriteMetrics(tier, bodyRadius);
  if (!metrics) return 0;
  var frac = liftFrac != null ? Number(liftFrac) : 0.25;
  if (!isFinite(frac) || frac < 0) frac = 0.25;
  return Math.max(0, metrics.drawH * frac);
}

/**
 * @param {HTMLElement} stackEl
 * @param {number[]} queueTiers upcoming tiers (first = next after current)
 * @param {number} alpha 0–1 panel fade
 */
export function updateJarDropQueue(stackEl, queueTiers, alpha) {
  if (!stackEl) return;
  var list = queueTiers || [];
  var a = alpha != null ? alpha : 1;
  var pill = stackEl.closest(".jar-drop-queue");
  // Fully faded out — wipe and hide.
  if (a <= 0.01) {
    if (stackEl.innerHTML !== "") stackEl.innerHTML = "";
    stackEl.removeAttribute("data-sig");
    if (pill) {
      pill.classList.add("hidden");
      pill.style.opacity = "";
    }
    return;
  }
  // Queue just emptied but still fading: keep the last rendered icon and let the
  // `alpha` (DROP_QUEUE_PANEL_FADE_MS) drive opacity down — fades away like the
  // hearts panel instead of vanishing instantly.
  if (list.length === 0) {
    if (pill) {
      pill.classList.remove("hidden");
      pill.style.opacity = String(a);
    }
    return;
  }
  if (pill) pill.classList.remove("hidden");
  pill.style.opacity = String(a);

  var chain = (typeof C.getActiveEvolutionChain === "function")
    ? C.getActiveEvolutionChain()
    : (C.EVOLUTION_CHAIN || []);

  function tierSrc(tier) {
    var cfg = chain[tier] || {};
    return mergeTierImageUrl(cfg.assetId || "cell");
  }

  // Only render peeks for tiers actually queued — never pad the stack with
  // duplicates of the last item (a 1-item queue must show exactly one icon).
  var peeks = [];
  if (list.length > 2) {
    peeks.push({ cls: "jar-drop-queue__peek jar-drop-queue__peek--behind-2", src: tierSrc(list[2]) });
  }
  if (list.length > 1) {
    peeks.push({ cls: "jar-drop-queue__peek jar-drop-queue__peek--behind-1", src: tierSrc(list[1]) });
  }
  peeks.push({ cls: "jar-drop-queue__peek jar-drop-queue__peek--next", src: tierSrc(list[0]) });

  // Runs every frame — skip when nothing changed.
  var sig = list.slice(0, 3).join(",");
  if (stackEl.getAttribute("data-sig") === sig) return;
  stackEl.setAttribute("data-sig", sig);

  // Reconcile in place rather than rebuilding innerHTML: replacing the <img>
  // elements wholesale makes fresh nodes paint blank for a frame (the cached
  // image must re-decode), so the queue appears to blink out and back on each
  // drop. Reusing the existing nodes and only updating src/class avoids that.
  var imgs = stackEl.querySelectorAll("img");
  while (imgs.length > peeks.length) {
    stackEl.removeChild(imgs[imgs.length - 1]);
    imgs = stackEl.querySelectorAll("img");
  }
  while (imgs.length < peeks.length) {
    var im = document.createElement("img");
    im.alt = "";
    stackEl.appendChild(im);
    imgs = stackEl.querySelectorAll("img");
  }
  for (var i = 0; i < peeks.length; i++) {
    var node = imgs[i];
    if (node.className !== peeks[i].cls) node.className = peeks[i].cls;
    if (node.getAttribute("src") !== peeks[i].src) node.setAttribute("src", peeks[i].src);
  }
}

export function updateJarDropPreview(previewEl, tier) {
  if (!previewEl) return;
  if (tier == null || tier < 0) {
    if (previewEl.innerHTML !== "") previewEl.innerHTML = "";
    previewEl.removeAttribute("data-tier");
    previewEl.classList.add("hidden");
    return;
  }
  previewEl.classList.remove("hidden");
  // This runs every render frame. Rebuilding innerHTML each time would recreate
  // the <img> continuously and restart its CSS pop animation, leaving the piece
  // frozen on the first keyframe (tiny + half-transparent). Only rebuild when the
  // tier actually changes so the existing element keeps its settled state.
  if (previewEl.getAttribute("data-tier") === String(tier)) return;
  var chain = (typeof C.getActiveEvolutionChain === "function")
    ? C.getActiveEvolutionChain()
    : (C.EVOLUTION_CHAIN || []);
  var cfg = chain[tier] || {};
  previewEl.setAttribute("data-tier", String(tier));
  previewEl.innerHTML =
    '<img class="jar-body jar-body--drop" src="' + mergeTierImageUrl(cfg.assetId || "cell") + '" alt="">';
}
